// import { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import { useFilterStore } from '../../stores/filter-store';
import { useOperationFilterStore } from '../../stores/operation-filter-store';

const countries = [
  { code: 'UK', label: 'UK', flag: '/img/country/uk.svg' },
  { code: 'US', label: 'US', flag: '/img/country/us.svg' },
  { code: 'GF', label: 'GF', flag: '/img/country/gf.svg' },
  { code: 'EU', label: 'EU', flag: '/img/country/eu.svg' },
  { code: 'TR', label: 'TR', flag: '/img/country/tr.svg' },
];

interface CountryFilterProps {
  selected?: string[];
  onSelect?: (code: string) => void;
  context?: 'dashboard' | 'fulfillment' | 'operation';
}

export default function CountryFilter({ selected, onSelect, context = 'dashboard' }: CountryFilterProps) {
  const { 
    filters, 
    fulfillmentFilters, 
    toggleMarketplace, 
    toggleFulfillmentMarketplace 
  } = useFilterStore();
  
  const {
    marketplaceFilter: operationMarketplaceFilter,
    toggleMarketplace: toggleOperationMarketplace
  } = useOperationFilterStore();
  
  // Get the current filters from the appropriate store
  const currentFilters = context === 'fulfillment' 
    ? fulfillmentFilters.marketplaceFilter 
    : context === 'operation'
    ? operationMarketplaceFilter
    : filters.marketplaceFilter;
  
  // Convert marketplace filters to country codes for display
  const getSelectedCountries = () => {
    if (context === 'dashboard') {
      // Convert 'uk_mkt' format to 'UK' format for display
      return currentFilters.map(filter => filter.replace('_mkt', '').toUpperCase());
    } else {
      // Fulfillment also uses 'uk_mkt' format, convert to 'UK' for display
      return currentFilters.map(filter => filter.replace('_mkt', '').toUpperCase());
    }
  };
  
  // Use the store state directly instead of local state
  const selectedCountries = selected || getSelectedCountries();
  const isCompactContext = context === 'fulfillment' || context === 'operation';

  const containerClass = isCompactContext ? 'flex flex-wrap items-center gap-2' : 'flex flex-wrap gap-3 items-center';
  const buttonClass = isCompactContext
    ? 'flex min-w-[72px] shrink items-center gap-1.5 px-2.5 py-1.5 rounded-xl border text-[13px] leading-none font-medium whitespace-nowrap transition-colors'
    : 'flex min-w-[80px] shrink items-center gap-2 px-2.5 py-1.5 rounded-2xl border text-[14px] leading-none font-medium whitespace-nowrap transition-colors';
  const checkboxClass = isCompactContext
    ? 'h-5 w-5 shrink-0 aspect-square rounded-md border'
    : 'h-7 w-7 shrink-0 aspect-square rounded-lg border';
  const flagClass = isCompactContext
    ? 'h-6 w-6 shrink-0 aspect-square object-contain'
    : 'h-[27.5px] w-[27.5px] shrink-0 aspect-square object-contain';

  const handleCountryToggle = (countryCode: string) => {
    // Update the appropriate store
    if (context === 'fulfillment') {
      toggleFulfillmentMarketplace(countryCode);
      // For fulfillment, the filtering happens automatically via the useFulfillment hook
      // which watches the fulfillmentFilters changes
    } else if (context === 'operation') {
      const marketplaceCode = `${countryCode.toLowerCase()}_mkt`;
      toggleOperationMarketplace(marketplaceCode);
    } else {
      toggleMarketplace(countryCode);
    }
    
    // Call the onSelect callback if provided
    onSelect?.(countryCode);
  };

  return (
    <div className={containerClass}>
      {countries.map((country) => {
        const isSelected = selectedCountries.includes(country.code);
        return (
          <button
            key={country.code}
            className={`${buttonClass} 
              ${isSelected
                ? 'bg-primary-100 text-primary-500 border-primary-500'
                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}
            `}
            onClick={() => handleCountryToggle(country.code)}
          >
            {/* Checkbox with rounded background */}
            <div className={`${checkboxClass} flex items-center justify-center ${
              isSelected 
                ? 'bg-primary-500 border-primary-500' 
                : 'bg-white border-gray-300'
            }`}>
              {isSelected && <Check size={12} className="text-white" />}
            </div>
            
            <span className="shrink-0">{country.label}</span>
            <img 
              src={country.flag} 
              alt={`${country.label} flag`}
              className={flagClass}
            />
          </button>
        );
      })}
    </div>
  );
} 